<template>
  <section class="speakers">
    <p class="meeting-section-intro">
      <template v-if="readonly">
        Les voix du mot à mot et les noms que le relecteur leur a donnés.
      </template>
      <template v-else>
        Donnez un nom aux voix pour que le mot à mot et les livrables les désignent : écoutez un extrait, saisissez
        le nom et le rôle, validez par Entrée. Les voix les plus présentes viennent en premier ; celles de quelques
        secondes sont souvent du bruit de découpage, inutile de les nommer. Deux voix d’une même personne ? Donnez-leur
        le même nom : leurs prises de parole seront réunies.
      </template>
    </p>

    <ul class="speakers-list">
      <li v-for="speaker in speakers" :key="speaker.raw" class="speakers-row">
        <span class="meeting-speech__speaker" :class="`meeting-speech__speaker--${(speaker.index - 1) % 6}`">
          {{ speaker.label }}
        </span>
        <span class="speakers-row__stats">{{ stats(speaker) }}</span>
        <button
          v-if="recordingUrl && speaker.sample"
          type="button"
          class="meetings-outline meetings-outline--small speakers-row__play"
          :title="playingId === speaker.raw ? 'Arrêter l’écoute' : 'Écouter un extrait de cette voix'"
          @click="togglePlay(speaker)"
        >
          <i :class="playingId === speaker.raw ? 'fas fa-stop' : 'fas fa-play'" aria-hidden="true"></i>
          {{ formatTimecode(speaker.sample.start) }}
        </button>
        <span class="speakers-row__field">
          <input
            v-model="names[speaker.raw]"
            type="text"
            class="speakers-row__input"
            :class="{ 'speakers-row__input--saved': isSaved(speaker.raw) }"
            :maxlength="maxLength"
            :placeholder="readonly ? 'Non nommé' : 'Nom et rôle, ex. Sophie Lambert, secrétaire'"
            :disabled="readonly || saving"
            :aria-label="`Nom du ${speaker.label}`"
            @keydown.enter.prevent="commit(speaker.raw, $event)"
            @blur="commit(speaker.raw)"
          >
          <i v-if="isSaved(speaker.raw)" class="fas fa-check speakers-row__saved" aria-hidden="true" title="Nom enregistré"></i>
        </span>
      </li>
    </ul>

    <audio
      v-if="recordingUrl"
      ref="player"
      :src="recordingUrl"
      preload="metadata"
      @timeupdate="onTimeUpdate"
      @pause="onPause"
      @ended="onPause"
    ></audio>
  </section>
</template>

<script>
import { formatDuration, formatTimecode, speakerSummary } from './meetingHelpers'
import recordingPlayback from './recordingPlayback'

/** Longueur maximale de l'extrait joué pour reconnaître une voix. */
const SAMPLE_SECONDS = 20

/** Même limite que l'API (NameMeetingSpeakersRequest::MAX_NAME_LENGTH). */
const MAX_NAME_LENGTH = 80

/**
 * Cadre de nommage des voix du mot à mot par le relecteur : une ligne par
 * voix, les plus présentes en premier, avec son temps de parole, l'écoute de
 * sa plus longue prise de parole et un champ pour son nom. Un nom validé
 * (Entrée ou sortie du champ) est envoyé aussitôt avec les autres (`save`,
 * objet « étiquette brute → nom ») ; le parent enregistre et renvoie la
 * réunion mise à jour, ce qui marque le champ comme enregistré.
 */
export default {
  name: 'app-meeting-speakers',
  mixins: [recordingPlayback],
  props: {
    meeting: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    saving: { type: Boolean, default: false }
  },
  data () {
    return {
      names: {},
      saved: {},
      lastSent: '',
      maxLength: MAX_NAME_LENGTH
    }
  },
  computed: {
    segments () {
      return Array.isArray(this.meeting.transcript_segments) ? this.meeting.transcript_segments : []
    },
    speakers () {
      return speakerSummary(this.segments)
    },
    recordingUrl () {
      return this.meeting.recording_url || ''
    },
    storedNames () {
      const stored = this.meeting.speaker_names && typeof this.meeting.speaker_names === 'object' ? this.meeting.speaker_names : {}
      const names = {}
      this.speakers.forEach(speaker => {
        names[speaker.raw] = String(stored[speaker.raw] || '').trim()
      })
      return names
    }
  },
  watch: {
    'meeting.uuid' () {
      this.reset()
    },
    /**
     * Après un enregistrement (ou un rechargement de la fiche), seuls les noms
     * qui ont changé côté serveur remplacent ce qui est saisi : un champ en
     * cours de frappe n'est pas écrasé.
     */
    storedNames (stored) {
      Object.keys(stored).forEach(raw => {
        if (stored[raw] !== this.saved[raw]) {
          this.$set(this.names, raw, stored[raw])
        }
      })
      this.saved = Object.assign({}, stored)
    }
  },
  created () {
    this.reset()
  },
  methods: {
    formatTimecode,
    reset () {
      this.saved = Object.assign({}, this.storedNames)
      this.names = Object.assign({}, this.storedNames)
      this.lastSent = ''
    },
    stats (speaker) {
      const turns = `${speaker.turns} prise${speaker.turns > 1 ? 's' : ''} de parole`
      const time = formatDuration(speaker.seconds) || 'moins d’une seconde'
      return `${time} · ${turns}`
    },
    isSaved (raw) {
      return !!this.saved[raw] && String(this.names[raw] || '').trim() === this.saved[raw]
    },
    commit (raw, event) {
      if (event && event.target && typeof event.target.blur === 'function') {
        event.target.blur()
      }
      if (this.readonly || this.saving) {
        return
      }
      const payload = {}
      this.speakers.forEach(speaker => {
        payload[speaker.raw] = String(this.names[speaker.raw] || '').trim()
      })
      const unchanged = Object.keys(payload).every(key => payload[key] === (this.saved[key] || ''))
      const serialized = JSON.stringify(payload)
      if (unchanged || serialized === this.lastSent) {
        return
      }
      this.lastSent = serialized
      this.$emit('save', payload)
    },
    togglePlay (speaker) {
      if (this.playingId === speaker.raw) {
        this.stopPlaying()
        return
      }
      const start = speaker.sample.start
      this.playRange(speaker.raw, start, Math.min(speaker.sample.end, start + SAMPLE_SECONDS))
    }
  }
}
</script>

<style scoped>
.speakers {
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.speakers-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.speakers-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
}

.speakers-row__stats {
  min-width: 11rem;
  color: #6b7280;
  font-size: 0.8125rem;
}

.speakers-row__play {
  white-space: nowrap;
}

.speakers-row__field {
  display: flex;
  flex: 1 1 18rem;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.speakers-row__input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.45rem 0.7rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.speakers-row__input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.speakers-row__input:disabled {
  background: #f9fafb;
  color: #6b7280;
}

.speakers-row__input--saved {
  border-color: #22c55e;
  background: #f0fdf4;
  color: #166534;
}

.speakers-row__saved {
  color: #16a34a;
  font-size: 0.8rem;
}
</style>
